import { FC, useCallback } from 'react';

import { ListScrollerSection } from '@rikstv/play-common/src/components/swimlane/listScroller/ListScroller';
import { MenuDetails, SwimlaneItem } from '@rikstv/play-common/src/forces/assetDetails/assetDetails.types';
import { commonRoutes } from '@rikstv/play-common/src/router/router.path';
import { RequiredProps } from '@rikstv/play-common/src/utils/types/typeUtils';

import { FadeIn } from '../../../components/animation/fade-in/FadeIn';
import { SwimlanePlaceholder } from '../../../components/swimlane/swimlanePlaceholder/SwimlanePlaceholder';
import { useSwimlane } from '../../../components/swimlane/useSwimlane';
import { getPlaceholderHeight } from '../../../components/swimlane/utils/getPlaceholderHeight';
import { Swimlane } from '../../pages/types';
import { ContinueWatchingItem } from '../styles/ContinueWatchingItem';
import { FeaturedItem } from '../styles/FeaturedItem';
import { LiveChannelItem } from '../styles/LiveChannelItem';
import { MenuSwimlaneItem } from '../styles/MenuSwimlaneItem';
import { ContextMenuItem, StandardItem } from '../styles/StandardItem';

type Item = MenuDetails | SwimlaneItem;

export interface BasicCoreScrollSwimlaneProps {
  swimlane: Swimlane;
  onItemClicked?: (item: Item, index: number) => void;
  contextMenuItems?: ContextMenuItem[];
}

export const BasicCoreScrollSwimlane: FC<BasicCoreScrollSwimlaneProps> = ({
  swimlane,
  onItemClicked,
  contextMenuItems,
}) => {
  const { swimlaneItems, menuItems, error, filterSwimlaneItemById } = useSwimlane(swimlane);

  const onClick = useCallback(
    (item: Item, elementIndex: number) => {
      onItemClicked?.(item, elementIndex);
    },
    [onItemClicked]
  );

  // Return null on error or when returned list has zero items
  if (error || swimlaneItems?.length === 0 || menuItems?.length === 0) {
    return null;
  }

  if (swimlaneItems === undefined && menuItems === undefined) {
    return <SwimlanePlaceholder height={getPlaceholderHeight(swimlane.style)} />;
  }

  const isMenuItems = swimlane.type === 'Menu' && menuItems;
  const isFeaturedItems = swimlane.style === 'Featured' && swimlaneItems;
  const isHeroItems = swimlane.style === 'Hero' && swimlaneItems;
  const isLiveChannels = swimlane.type === 'OnTvNow' && swimlane.style === 'Live' && swimlaneItems;
  const isStandardItems =
    !isFeaturedItems && !isHeroItems && !isLiveChannels && swimlane.type === 'Default' && swimlaneItems;
  const isContinueWatchingItems = swimlane.type === 'ContinueWatching' && swimlaneItems;
  const isMyListItems = swimlane.type === 'MyList' && swimlaneItems;
  const isPortrait = swimlane.style === 'StandardPortrait';

  return (
    <FadeIn>
      <ListScrollerSection swimlane={swimlane}>
        {/* menu items */}
        {isMenuItems &&
          menuItems.map((item, index) => (
            <MenuSwimlaneItem
              key={'menu-item-' + item.id}
              {...item}
              href={commonRoutes.collections({ slug: item.slug })}
              style={swimlane.style}
              onClick={() => onClick(item, index)}
            />
          ))}
        {/* standard items */}
        {isStandardItems &&
          swimlaneItems.map((item, index) => (
            <StandardItem
              key={'standard-item-' + item.id}
              asset={item}
              swimlaneType={swimlane.type}
              onClick={() => onClick(item, index)}
              noMargin
              contextMenuItems={contextMenuItems}
              portrait={isPortrait}
            />
          ))}

        {/* featured items */}
        {isFeaturedItems &&
          swimlaneItems.map((item, index) => (
            <FeaturedItem
              key={'featured-item-' + item.id}
              asset={item}
              swimlaneType={swimlane.type}
              onClick={() => onClick(item, index)}
              noMargin
            />
          ))}

        {/* live channel items */}
        {isLiveChannels &&
          (swimlaneItems as RequiredProps<SwimlaneItem, 'channel'>[]).map((item, index) => (
            <LiveChannelItem asset={item} key={'channel-item-' + item.id} onClick={() => onClick(item, index)} />
          ))}

        {/* continue watching items */}
        {isContinueWatchingItems &&
          swimlaneItems.map((item, index) => (
            <ContinueWatchingItem
              swimlane={swimlane}
              key={'continue-item-' + item.id}
              asset={item}
              preventNavigation={false}
              setOptimisticFilter={filterSwimlaneItemById}
              onClick={() => onClick(item, index)}
            />
          ))}

        {/* MyList items */}
        {isMyListItems &&
          swimlaneItems.map((item, index) => (
            <StandardItem
              key={'standard-item-' + item.id}
              asset={item}
              swimlaneType={swimlane.type}
              onClick={() => onClick(item, index)}
              noMargin
            />
          ))}
      </ListScrollerSection>
    </FadeIn>
  );
};
